import { useEffect, useState, useCallback, useMemo } from "react";
import { BaseWidget } from "../../components/liveReport/reportBuilder2/widgets";
import { Viewer } from "../../components/liveReport/reportBuilder2/editor/Viewer";
import Location from "../../hooks/Location";
import { deserializeWidget } from "../../components/liveReport/reportBuilder2/utils/serializer.js";
import { ReportBrandingContext } from "../../components/liveReport/reportBuilder2/context/ReportBrandingContext";
import { ReportPeriodContext } from "../../components/liveReport/reportBuilder2/context/ReportPeriodContext";
import reportToPeriodRange from "../../helpers/liveReportHelpers/reportToPeriodRange";
import PublicReportContext from "../../StateManagment/PublicReportContext";
import { setPublicDefinitions } from "../../firebase/publicDefinitions";
import ReportPersonaContext from "../../StateManagment/ReportPersonaContext";

const codeStorageKey = (reportId) => `livereport-share-code-${reportId}`;

const PUBLIC_REPORT_ENDPOINT = process.env.NODE_ENV === 'production'
    ? '/api/livereport'
    : 'https://europe-west1-deccos-app.cloudfunctions.net/getPublicLiveReport2';

/**
 * @param {Response} res - Een afgewezen antwoord van getPublicLiveReport2
 * @returns {Promise<string>} De foutcode uit de body; '' als die niet te lezen is
 */
const readError = async (res) => {
    try {
        const body = await res.json();
        return typeof body?.error === 'string' ? body.error : '';
    } catch (e) {
        // Geen JSON: dan is het een andere weigering, die de algemene melding krijgt.
        return '';
    }
};

// De definities achter de widgets komen mee met getPublicLiveReport2
// (docs/features/public-livereport-data.md, fase 5). Tot alle gedeelde
// rapporten zonder gaten nagelopen zijn, leest de pagina standaard nog uit
// Firestore. `?definitiecheck=1` leest uit Firestore en meldt in de console wat
// de set mist; `?definitieset=1` leest alleen uit de set.
const USE_DEFINITION_SET = false;
/** @returns {'set'|'check'|'firestore'} */
export const definitionMode = (search) => {
    const params = new URLSearchParams(search);
    if (params.get('definitiecheck') === '1') return 'check';
    if (params.get('definitieset') === '1') return 'set';
    return USE_DEFINITION_SET ? 'set' : 'firestore';
};

const LiveReportOpen2 = () => {

    // Hooks
    const reportId = Location()[3];

    /** @type {ReturnType<typeof useState<BaseWidget[]>>} */
    const [widgets, setWidgets] = useState([]);
    /** @type {ReturnType<typeof useState<boolean>>} */
    const [loading, setLoading] = useState(true);
    /** @type {ReturnType<typeof useState<boolean>>} */
    const [codeRequired, setCodeRequired] = useState(false);
    /** @type {ReturnType<typeof useState<boolean>>} */
    const [lockedOut, setLockedOut] = useState(false);
    /** @type {ReturnType<typeof useState<string|null>>} */
    const [error, setError] = useState(null);
    /** @type {ReturnType<typeof useState<string>>} */
    const [inputCode, setInputCode] = useState('');
    /** @type {ReturnType<typeof useState<boolean>>} */
    const [submitting, setSubmitting] = useState(false);
    /** @type {ReturnType<typeof useState<string>>} */
    const [ownerLogo, setOwnerLogo] = useState('');
    /** @type {ReturnType<typeof useState<string>>} */
    const [ownerName, setOwnerName] = useState('');
    /** @type {ReturnType<typeof useState<{primary: string, secundairy: string, tertairy: string}>>} */
    const [ownerColors, setOwnerColors] = useState({ primary: '', secundairy: '', tertairy: '' });
    /** @type {ReturnType<typeof useState<{start: string|null, end: string|null}|null>>} */
    const [reportPeriod, setReportPeriod] = useState(/** @type {{start: string|null, end: string|null}|null} */ (null));
    // De deelnemer van het rapport; komt van de server mee (T11). Ontbreekt
    // het veld, dan is het een gewoon rapport → useWidgetPersona trekt dat gelijk.
    // AI-CONTEXT: See docs/features/livereport-per-deelnemer.md
    /** @type {ReturnType<typeof useState<string|null>>} */
    const [reportPersonaId, setReportPersonaId] = useState(/** @type {string|null} */ (null));
    /** @type {ReturnType<typeof useState<string>>} */
    const [acceptedCode, setAcceptedCode] = useState('');
    // De teksten achter de quote- en anekdotewidgets van dit rapport. Ze komen
    // mee met de functie, want Quotes en Anecdotes zijn alleen voor het eigen
    // team leesbaar (inbox #320) → docs/features/public-livereport-data.md (fase 3)
    const [storytelling, setStorytelling] = useState(
        /** @type {{quotes: Array<object>, anecdotes: Array<object>}} */ ({ quotes: [], anecdotes: [] })
    );
    // De gegevens achter de deelnemerwidgets (aanwezigheid, doelen, traject).
    // De persona-collecties zijn alleen voor het eigen team leesbaar, dus ze
    // komen mee met de functie; `null` = geen deelnemer op dit rapport.
    // AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T12)
    const [personaData, setPersonaData] = useState(
        /** @type {import('../../StateManagment/PublicReportContext').PublicPersonaData|null} */ (null)
    );
    // Niemand is hier ingelogd, dus de blokken halen hun antwoorden via de
    // openbare functie op, met hetzelfde rapport-ID en dezelfde code als de
    // opbouw → docs/features/public-livereport-data.md
    const publicReport = useMemo(
        () => ({ reportId, code: acceptedCode, storytelling, personaData }),
        [reportId, acceptedCode, storytelling, personaData]
    );
    // De huisstijl reist mee vanaf de server: hier is niemand ingelogd, dus
    // `useSettings()` kan de kleuren van de eigenaar niet lezen en zouden alle
    // grafieken in het generieke roze/cyaan komen → docs/inbox.md #246
    const brandingValue = useMemo(() => ({
        logo: ownerLogo,
        compagnyName: ownerName,
        primaryColor: ownerColors.primary,
        secundairyColor: ownerColors.secundairy,
        tertairyColor: ownerColors.tertairy,
    }), [ownerLogo, ownerName, ownerColors]);

    const fetchReport = useCallback(async (code) => {
        setError(null);
        try {
            const res = await fetch(PUBLIC_REPORT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reportId, code }),
            });

            if (res.ok) {
                const data = await res.json();
                const parsed = (data.widgets || [])
                    .map(w => deserializeWidget(w, true))
                    .filter(w => !w.deleted);
                // Eerst de definities, dan de widgets: die lezen bij hun eerste
                // render al uit de set (zie definitionMode bovenaan).
                const mode = definitionMode(window.location.search);
                setPublicDefinitions(mode === 'firestore' ? null : (data.definitions || {}), { check: mode === 'check' });
                setWidgets(parsed);
                setStorytelling({
                    quotes: Array.isArray(data.storytelling?.quotes) ? data.storytelling.quotes : [],
                    anecdotes: Array.isArray(data.storytelling?.anecdotes) ? data.storytelling.anecdotes : [],
                });
                setOwnerLogo(data.report?.Logo || '');
                setOwnerName(data.report?.CommunityName || '');
                setOwnerColors({
                    primary: data.report?.PrimaryColor || '',
                    secundairy: data.report?.SecundairyColor || '',
                    tertairy: data.report?.TertairyColor || '',
                });
                setPersonaData(data.personaData ?? null);
                setReportPeriod(reportToPeriodRange(data.report));
                setReportPersonaId(data.report?.PersonaID ?? null);
                setAcceptedCode(code || '');
                setCodeRequired(false);
                setLockedOut(false);
                setLoading(false);
                if (code) {
                    window.localStorage.setItem(codeStorageKey(reportId), code);
                }
                return;
            }

            if (res.status === 412) {
                setCodeRequired(true);
                setLoading(false);
            } else if (res.status === 401) {
                window.localStorage.removeItem(codeStorageKey(reportId));
                setCodeRequired(true);
                setError('Onjuiste code. Probeer het opnieuw.');
                setLoading(false);
            } else if (res.status === 429) {
                setLockedOut(true);
                setError('Te veel pogingen. Probeer het over 10 minuten opnieuw.');
                setLoading(false);
            } else if (res.status === 403 && (await readError(res)) === 'persona-report-needs-code') {
                // Een rapport over één deelnemer zonder ingestelde code: de server
                // weigert (T11). Er is geen code om in te vullen, dus geen formulier.
                setError('Deze rapportage gaat over één deelnemer en is alleen te openen met een toegangscode. Vraag de maker van de rapportage om er een in te stellen.');
                setLoading(false);
            } else if (res.status === 404) {
                setError('Deze rapportage is niet beschikbaar.');
                setLoading(false);
            } else {
                setError('Kon de rapportage niet laden.');
                setLoading(false);
            }
        } catch (e) {
            console.error('LiveReportOpen2 fetch failed:', e);
            setError('Kon de rapportage niet laden.');
            setLoading(false);
        }
    }, [reportId]);

    // Buiten deze pagina leest alles weer gewoon uit Firestore.
    useEffect(() => () => setPublicDefinitions(null), []);

    useEffect(() => {
        if (!reportId) return;
        setLoading(true);
        const stored = window.localStorage.getItem(codeStorageKey(reportId));
        fetchReport(stored || undefined);
    }, [reportId, fetchReport]);

    const submitCode = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(inputCode)) {
            setError('Voer de 6-cijferige code in.');
            return;
        }
        setSubmitting(true);
        await fetchReport(inputCode);
        setSubmitting(false);
    };

    if (loading) {
        return <p className="share-code-gate-status">Laden...</p>;
    }

    if (lockedOut) {
        return (
            <div className="share-code-gate">
                <h2>Te veel pogingen</h2>
                <p>{error}</p>
            </div>
        );
    }

    if (codeRequired) {
        return (
            <form className="share-code-gate" onSubmit={submitCode}>
                <h2>Beveiligde rapportage</h2>
                <p>Voer de 6-cijferige toegangscode in om deze rapportage te openen.</p>
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    value={inputCode}
                    onChange={(ev) => setInputCode(ev.target.value.replace(/\D/g, ''))}
                    autoFocus
                />
                {error && <p className="share-code-gate-error">{error}</p>}
                <button type="submit" className="button-simple" disabled={submitting}>
                    {submitting ? 'Bezig...' : 'Open rapportage'}
                </button>
            </form>
        );
    }

    if (error) {
        return <p className="share-code-gate-status share-code-gate-status-error">{error}</p>;
    }

    return (
        <ReportBrandingContext.Provider value={brandingValue}>
            <ReportPeriodContext.Provider value={reportPeriod}>
                <ReportPersonaContext.Provider value={reportPersonaId}>
                    <PublicReportContext.Provider value={publicReport}>
                        <Viewer widgets={widgets} />
                    </PublicReportContext.Provider>
                </ReportPersonaContext.Provider>
            </ReportPeriodContext.Provider>
        </ReportBrandingContext.Provider>
    );
}

export default LiveReportOpen2;
